import React, { useEffect } from 'react';
import { X } from 'lucide-react';

// Shared modal shell: backdrop, Escape-to-close, focus-safe close button,
// and the app's standard rounded/shadow/border treatment. Centralizes the
// pattern ChatWidget originated so any future dialog looks consistent
// instead of each one rebuilding its own overlay.
const Modal = ({ open, onClose, title, children, labelledBy, className = '' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-emerald-950/40 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={!labelledBy ? title : undefined}
        aria-labelledby={labelledBy}
        className={`relative w-full max-w-md bg-white rounded-3xl border border-emerald-100 shadow-floating overflow-hidden animate-fade-in ${className}`}
        style={{ animationDuration: '200ms' }}
      >
        {title && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-emerald-50">
            <h2 className="font-bold text-emerald-950">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default Modal;
